import { IRevenueSchedulePeriod } from '@monetize/types/app';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { addMonths } from 'date-fns/addMonths';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchRevenueSchedulePeriodsByRange,
  revenueKeys,
} from '../../api/revenueService';

const DEFAULT_CHUNK_MONTHS = 12;
const ISO_DATE_FORMAT = 'yyyy-MM-dd';

interface PeriodWindow {
  windowStart: string;
  windowEnd: string;
}

interface UseRevenueSchedulePeriodsInfiniteParams {
  scheduleId: string;
  contractStart?: string | null;
  contractEndInclusive?: string | null;
  chunkMonths?: number;
}

const toIsoDate = (iso: string) => format(parseISO(iso), ISO_DATE_FORMAT);

const buildAllWindows = (
  contractStart: string,
  contractEndInclusive: string,
  chunkMonths: number,
): PeriodWindow[] => {
  const start = parseISO(contractStart);
  const end = parseISO(contractEndInclusive);
  if (start > end) {
    return [];
  }
  const windows: PeriodWindow[] = [];
  let cursor = start;
  while (cursor <= end) {
    const tentativeEnd = addMonths(cursor, chunkMonths);
    const windowEnd = tentativeEnd > end ? end : tentativeEnd;
    windows.push({
      windowStart: format(cursor, ISO_DATE_FORMAT),
      windowEnd: format(windowEnd, ISO_DATE_FORMAT),
    });
    if (tentativeEnd > end) {
      break;
    }
    cursor = addMonths(cursor, chunkMonths);
  }
  return windows;
};

export interface RevenueSchedulePeriodsInfiniteResult {
  periods: IRevenueSchedulePeriod[];
  loadedThrough: string | null;
  isInitialLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  loadNextWindow: () => void;
  loadAll: () => Promise<void>;
}

export function useRevenueSchedulePeriodsInfinite({
  scheduleId,
  contractStart,
  contractEndInclusive,
  chunkMonths = DEFAULT_CHUNK_MONTHS,
}: UseRevenueSchedulePeriodsInfiniteParams): RevenueSchedulePeriodsInfiniteResult {
  const queryClient = useQueryClient();

  const allWindows = useMemo(() => {
    if (!contractStart || !contractEndInclusive) {
      return [];
    }
    return buildAllWindows(
      toIsoDate(contractStart),
      toIsoDate(contractEndInclusive),
      chunkMonths,
    );
  }, [contractStart, contractEndInclusive, chunkMonths]);

  // Number of windows queued for fetching. Starts at 1 so the first window is
  // queried on the very first render — otherwise consumers would briefly see
  // an empty table before an effect could kick the first fetch off.
  const [loadedWindowCount, setLoadedWindowCount] = useState(1);

  // Reset back to the first window whenever the schedule or contract dates
  // change so a reused component doesn't carry over the prior scroll depth.
  const currentKey = `${scheduleId}|${contractStart ?? ''}|${
    contractEndInclusive ?? ''
  }|${chunkMonths}`;
  useEffect(() => {
    setLoadedWindowCount(1);
  }, [currentKey]);

  const activeWindows = useMemo(
    () => allWindows.slice(0, Math.min(loadedWindowCount, allWindows.length)),
    [allWindows, loadedWindowCount],
  );

  const queries = useQueries({
    queries: activeWindows.map((window) => ({
      queryKey: revenueKeys.revenueSchedulePeriodsRange(scheduleId, window),
      queryFn: () => fetchRevenueSchedulePeriodsByRange(scheduleId, window),
      enabled: !!scheduleId,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    })),
  });

  const periods = useMemo(() => {
    const byId = new Map<string, IRevenueSchedulePeriod>();
    for (const { data } of queries) {
      if (!data) {
        continue;
      }
      for (const period of data) {
        byId.set(period.id, period);
      }
    }
    return Array.from(byId.values());
  }, [queries]);

  // The latest contiguous window end whose query has finished successfully.
  // We only treat a window as "loaded" if all preceding ones are also loaded,
  // so the table can confidently render skeletons for everything after.
  const loadedThrough = useMemo(() => {
    let last: string | null = null;
    for (let i = 0; i < queries.length; i++) {
      const query = queries[i];
      const window = activeWindows[i];
      if (!window) {
        break;
      }
      if (query.isSuccess) {
        last = window.windowEnd;
      } else {
        break;
      }
    }
    return last;
  }, [queries, activeWindows]);

  const isInitialLoading =
    activeWindows.length > 0 && queries[0]?.isLoading === true;
  const isFetchingMore = queries.some(
    (query, index) => index > 0 && query.isFetching,
  );
  const hasMore = loadedWindowCount < allWindows.length;

  const loadNextWindow = useCallback(() => {
    setLoadedWindowCount((current) =>
      current < allWindows.length ? current + 1 : current,
    );
  }, [allWindows.length]);

  const loadAll = useCallback(async () => {
    if (allWindows.length === 0) {
      return;
    }
    setLoadedWindowCount(allWindows.length);
    await Promise.all(
      allWindows.map((window) =>
        queryClient.fetchQuery({
          queryKey: revenueKeys.revenueSchedulePeriodsRange(scheduleId, window),
          queryFn: () => fetchRevenueSchedulePeriodsByRange(scheduleId, window),
          staleTime: Infinity,
        }),
      ),
    );
  }, [allWindows, queryClient, scheduleId]);

  return {
    periods,
    loadedThrough,
    isInitialLoading,
    isFetchingMore,
    hasMore,
    loadNextWindow,
    loadAll,
  };
}
